import { HighlightDroppable } from "@/components/forms/components/HighlightDroppable.tsx";
import { CustomDraggableItem } from "@/pages/settings/custom-fields/components/CustomDraggableItem.tsx";
import {
  CustomDraggableNode,
  CustomDraggableNodeType,
} from "@/pages/settings/custom-fields/custom-fields.ts";
import { CustomFieldEditModal } from "@/pages/settings/custom-fields/widgets/CustomFieldEditModal.tsx";
import { CustomSectionEditModal } from "@/pages/settings/custom-fields/widgets/CustomSectionEditModal.tsx";
import { EmployeeCustomTableModal } from "@/pages/settings/custom-fields/widgets/EmployeeCustomTableModal.tsx";
import {
  ApiFieldsSort,
  useAllTables,
  useFieldsSort,
  useSectionFields,
  useSectionsByType,
  useTabByTypeAndId,
  useTabItemsSort,
} from "@/queries/use-custom-fields.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  ApiCFEntityTypes,
  ApiCFTabItemType,
  ApiCFTabSortItem,
  ApiCFTabType,
  ApiField,
  ApiSection,
  ApiTable,
} from "@/types/custom-field.types.ts";
import { DragDropContext, DragStart, DropResult } from "@hello-pangea/dnd";
import {
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { ApiFeatureType } from "@/types/subscription.types.ts";

interface CustomFieldListProps {
  tabId: number;
  entityType?: ApiCFEntityTypes;
}

export function CustomFieldList({
  tabId,
  entityType = ApiCFEntityTypes.employee,
}: CustomFieldListProps) {
  const isEmployee = entityType === ApiCFEntityTypes.employee;
  const tab = useTabByTypeAndId(tabId, entityType);
  const { data: sections } = useSectionsByType(entityType);
  const { data: fields } = useSectionFields(entityType);
  const { data: tables } = useAllTables(isEmployee);
  const { mutateAsync: tabItemsSort } = useTabItemsSort();
  const { mutateAsync: fieldsSort } = useFieldsSort();
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { isFeatureEnabled } = usePermissions();
  const canCreateCustomFields = isFeatureEnabled(ApiFeatureType.custom_fields);

  const [optimisticTabItems, setOptimisticTabItems] = useState<{
    source: ApiCFTabSortItem[] | undefined;
    items: ApiCFTabSortItem[];
  } | null>(null);
  const [optimisticFields, setOptimisticFields] = useState<{
    source: ApiField[] | undefined;
    items: ApiField[];
  } | null>(null);
  const [draggingSection, setDraggingSection] = useState<number | undefined>();
  const [draggingMandatoryField, setDraggingMandatoryField] = useState(false);

  const currentTabItems =
    optimisticTabItems?.source === tab?.items
      ? (optimisticTabItems?.items ?? tab?.items)
      : tab?.items;
  const currentFields =
    optimisticFields?.source === fields ? (optimisticFields?.items ?? fields) : fields;

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.customFields.sortingViewed);
  });

  const sectionsById = useMemo(() => {
    const map = new Map<number, ApiSection>();
    sections?.forEach((s) => map.set(s.id, s));
    return map;
  }, [sections]);

  const tablesById = useMemo(() => {
    const map = new Map<number, ApiTable>();
    tables?.forEach((tbl) => map.set(tbl.id, tbl));
    return map;
  }, [tables]);

  const computedNodes = useMemo(() => {
    const result: {
      items: CustomDraggableNode[];
      fieldsBySection: Map<number, CustomDraggableNode[]>;
    } = {
      items: [],
      fieldsBySection: new Map(),
    };

    if (!currentTabItems) return result;

    currentTabItems.forEach((item) => {
      if (item.item_type === ApiCFTabItemType.section) {
        const section = sectionsById.get(item.item_id);
        if (!section || section.is_archived) return;

        result.items.push({
          id: section.id,
          type: CustomDraggableNodeType.section,
          data: section,
        });

        const sectionFields = (currentFields ?? [])
          .filter((f) => f.section_id === section.id && !f.is_archived)
          .map((field) => ({
            id: field.id,
            type: CustomDraggableNodeType.field,
            data: field,
          }));
        result.fieldsBySection.set(section.id, sectionFields);
      } else if (item.item_type === ApiCFTabItemType.table) {
        const table = tablesById.get(item.item_id);
        if (!table) return;

        result.items.push({
          id: table.id,
          type: CustomDraggableNodeType.table,
          data: table,
        });
      }
    });

    return result;
  }, [currentTabItems, currentFields, sectionsById, tablesById]);

  const handleDragStart = useCallback(
    (initial: DragStart) => {
      if (initial.draggableId.startsWith("section-")) {
        const [, itemId] = initial.draggableId.split("-");
        setDraggingSection(parseInt(itemId, 10));
      }
      if (initial.draggableId.startsWith("field-")) {
        const [, itemId] = initial.draggableId.split("-");
        const field = currentFields?.find((f) => f.id === parseInt(itemId, 10));
        setDraggingMandatoryField(!!field?.is_mandatory && !field.is_hidden);
      }
    },
    [currentFields]
  );

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      setDraggingSection(undefined);
      setDraggingMandatoryField(false);

      const { destination, source, draggableId, type } = result;

      if (!destination || !tab) return;
      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return;
      }

      if (type === "field") {
        if (!currentFields) return;

        const [, fieldIdStr] = draggableId.split("-");
        const movedFieldId = parseInt(fieldIdStr, 10);
        const destSectionId = parseInt(destination.droppableId.split("-")[1], 10);

        const updatedFields = [...currentFields];

        const fieldIndex = updatedFields.findIndex((f) => f.id === movedFieldId);
        if (fieldIndex === -1) return;

        const [fieldToMove] = updatedFields.splice(fieldIndex, 1);
        const movedField = { ...fieldToMove, section_id: destSectionId };

        const targetSectionFields = updatedFields
          .map((field, index) => ({ field, originalIndex: index }))
          .filter(({ field }) => field.section_id === destSectionId && !field.is_archived);

        let insertIndex: number;
        if (targetSectionFields.length === 0) {
          insertIndex = updatedFields.length;
        } else if (destination.index === 0) {
          insertIndex = targetSectionFields[0].originalIndex;
        } else if (destination.index >= targetSectionFields.length) {
          insertIndex = targetSectionFields[targetSectionFields.length - 1].originalIndex + 1;
        } else {
          insertIndex = targetSectionFields[destination.index].originalIndex;
        }

        updatedFields.splice(insertIndex, 0, movedField);

        setOptimisticFields({ source: fields, items: updatedFields });

        const sortData: ApiFieldsSort[] = [];
        const sectionsWithFields = new Set(
          updatedFields.filter((f) => !f.is_archived).map((f) => f.section_id)
        );

        sectionsWithFields.forEach((sectionId) => {
          const sectionFields = updatedFields.filter(
            (f) => f.section_id === sectionId && !f.is_archived
          );
          if (sectionFields.length > 0) {
            sortData.push({
              section_id: sectionId,
              field_ids: sectionFields.map((f) => f.id),
            });
          }
        });

        try {
          await fieldsSort(sortData);
        } catch {
          setOptimisticFields(null);
        }
      } else if (type === "tabItem") {
        if (!currentTabItems) return;

        const visibleItems = currentTabItems.filter((it) => {
          if (it.item_type === ApiCFTabItemType.section) {
            const s = sectionsById.get(it.item_id);
            return !!s && !s.is_archived;
          }
          return tablesById.has(it.item_id);
        });
        const archivedItems = currentTabItems.filter((it) => !visibleItems.includes(it));

        const updated = [...visibleItems];
        const [moved] = updated.splice(source.index, 1);
        updated.splice(destination.index, 0, moved);

        setOptimisticTabItems({ source: tab.items, items: [...updated, ...archivedItems] });

        try {
          await tabItemsSort({
            type: tab.type,
            entityType,
            items: updated.map((it) => ({
              item_id: it.item_id,
              item_type: it.item_type,
            })),
          });
        } catch {
          setOptimisticTabItems(null);
        }
      }
    },
    [
      currentTabItems,
      currentFields,
      tab,
      tabItemsSort,
      fieldsSort,
      sectionsById,
      tablesById,
      fields,
      entityType,
    ]
  );

  if (!tab || !sections || !fields || (isEmployee && !tables)) {
    return (
      <Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
        <Stack className={"py-4 border-b border-dark/10"}>
          <Skeleton className={"h-6 w-full"} />
        </Stack>
      </Stack>
    );
  }

  const handleCreateField = () => {
    openModal(CustomFieldEditModal, { tabId, entityType });
  };

  const handleCreateSection = () => {
    openModal(CustomSectionEditModal, { tabType: tab.type, entityType });
  };

  const handleCreateTable = () => {
    openModal(EmployeeCustomTableModal, { tabId });
  };

  const isSidebar = tab.type === ApiCFTabType.sidebar;

  return (
    <Stack gap={"md"}>
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <HighlightDroppable droppableId={`tab-${tabId}`} type="tabItem" className={"rounded-none"}>
          {(provided) => (
            <Stack {...provided.droppableProps} ref={provided.innerRef}>
              {computedNodes.items.map((node, index) => (
                <CustomDraggableItem
                  key={`${node.type}-${node.id}`}
                  node={node}
                  index={index}
                  children={
                    node.type === CustomDraggableNodeType.section
                      ? computedNodes.fieldsBySection.get(node.id)
                      : undefined
                  }
                  draggingSection={draggingSection}
                  draggingMandatoryField={draggingMandatoryField}
                  tabType={tab.type}
                />
              ))}
              {provided.placeholder}
            </Stack>
          )}
        </HighlightDroppable>
      </DragDropContext>
      {canCreateCustomFields && (
        <DropdownMenu
          trigger={
            <Button
              className={"w-full"}
              size={"lg"}
              variant={"secondary"}
              leftIcon={<FontAwesomeIcon icon="fa-solid fa-plus" />}
            >
              {t("common:create")}...
            </Button>
          }
        >
          <DropdownMenuItem
            onClick={handleCreateField}
            title={t("custom_fields:new_field")}
            description={t("custom_fields:create_new_field")}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-pen-field" />}
          />
          <DropdownMenuItem
            onClick={handleCreateSection}
            title={t("custom_fields:new_section")}
            description={t("custom_fields:create_new_section")}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-folder" />}
          />
          {isEmployee &&
            (isSidebar ? (
              <DropdownMenuItem
                disabled={true}
                title={t("custom_fields:new_custom_table")}
                description={t("custom_fields:unavailable_for_this_page")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-lock" />}
              />
            ) : (
              <DropdownMenuItem
                onClick={handleCreateTable}
                title={t("custom_fields:new_custom_table")}
                description={t("custom_fields:create_new_custom_table")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-table" />}
              />
            ))}
        </DropdownMenu>
      )}
    </Stack>
  );
}
